import Button from '@/components/Common/Button';
import DataTable from '@/components/Common/DataTable';
import Search from '@/components/Common/Search';
import { Box, IconButton, Typography } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { useState, useMemo } from 'react';
import { GoPlus } from 'react-icons/go';
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  useGetAlternativeNames,
  useDeleteAlternativeName
} from '@/apis/business/setting/integrations/StreamOrderSettings/hook';

export default function AlternativeNames() {
  const navigate = useNavigate();
  const { integrationId } = useParams();

  const [searchText, setSearchText] = useState<string | null>(null);

  const { data: AlternativeNamesData, isLoading } = useGetAlternativeNames(Number(integrationId), {
    search: searchText,
    per_page: 9999
  });

  const { mutate: deleteAlternativeName } = useDeleteAlternativeName(Number(integrationId));

  const handleDelete = (id: number) => {
    deleteAlternativeName(id);
  };

  const memorizedAlternativeNames = useMemo(() => AlternativeNamesData?.data ?? [], [AlternativeNamesData?.data]);

  return (
    <>
      {/* Header */}
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography fontSize={16} fontWeight={500}>
          Alternative Names Configurations
        </Typography>

        <Box display='flex' gap={2}>
          <Search
            size='small'
            placeholder='Search'
            searchText={searchText}
            setSearchText={setSearchText}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            sx={{
              minWidth: { xs: '100%', sm: 220 },
              bgcolor: 'white',
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                minHeight: 36
              }
            }}
          />

          <Button variant='contained' startIcon={<GoPlus />} onClick={() => navigate('alternative-names/add')}>
            Add
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <DataTable data={memorizedAlternativeNames} columns={columns(handleDelete)} isLoading={isLoading} />
    </>
  );
}

const columns = (deleteRow: (id: number) => void): ColumnDef<any>[] => [
  {
    header: 'MENU',
    accessorKey: 'pos_menu.name'
  },
  {
    header: 'ITEM',
    accessorKey: 'pos_menu_item.name'
  },
  {
    header: 'ITEM ALT NAME',
    accessorKey: 'pos_menu_item.alternative_name',
    cell: ({ row }) => row?.original?.pos_menu_item?.alternative_name
  },
  {
    header: 'MODIFIER ALT NAMES',
    accessorKey: 'pos_modifiers',
    cell: ({ row }) =>
      row.original.pos_modifiers?.length
        ? row.original.pos_modifiers.map((m: any) => m.alternative_name).join(', ')
        : '-'
  },
  {
    header: 'ACTION',
    cell: ({ row }) => (
      <Box display='flex' gap={1}>
        <Link to={`alternative-names/${row.original.id}`}>
          <IconButton>
            <MdOutlineEdit />
          </IconButton>
        </Link>

        <IconButton color='error' onClick={() => deleteRow(row.original.id)}>
          <RiDeleteBin6Line />
        </IconButton>
      </Box>
    )
  }
];
